package com.pointlessapps.granite.mica.llvm

import com.pointlessapps.granite.mica.ast.expressions.ArrayLiteralExpression
import com.pointlessapps.granite.mica.ast.expressions.MapLiteralExpression
import com.pointlessapps.granite.mica.ast.expressions.SetLiteralExpression
import com.pointlessapps.granite.mica.ast.expressions.UnaryExpression
import com.pointlessapps.granite.mica.ast.expressions.TypeCoercionExpression
import com.pointlessapps.granite.mica.ast.expressions.AffixAssignmentExpression
import com.pointlessapps.granite.mica.ast.expressions.PrefixAssignmentExpression
import com.pointlessapps.granite.mica.ast.expressions.PostfixAssignmentExpression
import com.pointlessapps.granite.mica.ast.expressions.BinaryExpression
import com.pointlessapps.granite.mica.ast.expressions.BooleanLiteralExpression
import com.pointlessapps.granite.mica.ast.expressions.Expression
import com.pointlessapps.granite.mica.ast.expressions.FunctionCallExpression
import com.pointlessapps.granite.mica.ast.expressions.IfConditionExpression
import com.pointlessapps.granite.mica.ast.expressions.InterpolatedStringExpression
import com.pointlessapps.granite.mica.ast.expressions.NumberLiteralExpression
import com.pointlessapps.granite.mica.ast.expressions.StringLiteralExpression
import com.pointlessapps.granite.mica.ast.expressions.SymbolExpression
import com.pointlessapps.granite.mica.ast.statements.ExpressionStatement
import com.pointlessapps.granite.mica.model.Token

internal data class LlvmValue(val register: String, val type: String)

internal fun Expression.unfoldExpression(
    context: LlvmCompilerContext,
    builder: StringBuilder,
): LlvmValue = when (this) {
    is NumberLiteralExpression -> {
        val type = if (token.type == Token.NumberLiteral.Type.Real) "double" else "i64"
        val value = token.value
        LlvmValue(value, type) // Constants can be used directly as registers
    }

    is StringLiteralExpression -> {
        val strValue = token.value
        val name = context.declareStringConstant(strValue)
        LlvmValue(name, "ptr")
    }

    is BooleanLiteralExpression -> {
        val value = if (token.value == "true") "1" else "0"
        LlvmValue(value, "i1")
    }

    is InterpolatedStringExpression -> {
        // Evaluate all expressions
        val argVals = expressions.map { it.unfoldExpression(context, builder) }

        // Allocate a 1024 byte buffer for the concatenated string (PoC limit)
        val bufferReg = context.nextRegister()
        builder.append("  $bufferReg = call ptr @malloc(i64 1024)\n")

        // Construct the format string e.g. "%s%ld%s"
        val formatStrBuilder = StringBuilder()
        argVals.forEach {
            when (it.type) {
                "i64" -> formatStrBuilder.append("%ld")
                "double" -> formatStrBuilder.append("%f")
                "ptr" -> formatStrBuilder.append("%s")
                "i1" -> formatStrBuilder.append("%ld") // Assuming we zext i1 to i64
                else -> formatStrBuilder.append("%s")
            }
        }
        val formatName = context.declareStringConstant(formatStrBuilder.toString())

        // Construct sprintf arguments
        val argsStr = argVals.joinToString(", ") { valToPrint ->
            if (valToPrint.type == "i1") {
                val extReg = context.nextRegister()
                builder.append("  $extReg = zext i1 ${valToPrint.register} to i64\n")
                "i64 $extReg"
            } else {
                "${valToPrint.type} ${valToPrint.register}"
            }
        }

        val ignored = context.nextRegister()
        builder.append("  $ignored = call i32 (ptr, ptr, ...) @sprintf(ptr $bufferReg, ptr $formatName, $argsStr)\n")
        LlvmValue(bufferReg, "ptr")
    }

    is SymbolExpression -> {
        val info = context.getVariableInfo(token.value)
            ?: throw Exception("Undefined variable ${token.value}")
        val resultReg = context.nextRegister()
        builder.append("  $resultReg = load i64, ptr ${info.ptr}\n")
        
        if (info.type == "ptr") {
            val ptrReg = context.nextRegister()
            builder.append("  $ptrReg = inttoptr i64 $resultReg to ptr\n")
            LlvmValue(ptrReg, "ptr")
        } else {
            LlvmValue(resultReg, "i64")
        }
    }

    is UnaryExpression -> {
        val operandVal = rhs.unfoldExpression(context, builder)
        val resultReg = context.nextRegister()
        when (operatorToken.type) {
            Token.Operator.Type.Subtract -> {
                builder.append("  $resultReg = sub i64 0, ${operandVal.register}\n")
                LlvmValue(resultReg, "i64")
            }
            Token.Operator.Type.Not -> {
                // Assuming boolean is casted to i64 in our simplified logic
                val cmpReg = context.nextRegister()
                builder.append("  $cmpReg = icmp eq i64 ${operandVal.register}, 0\n")
                builder.append("  $resultReg = zext i1 $cmpReg to i64\n")
                LlvmValue(resultReg, "i64")
            }
            Token.Operator.Type.Add -> {
                // Unary plus does nothing
                operandVal
            }
            else -> throw Exception("Unsupported unary operator ${operatorToken.type}")
        }
    }

    is BinaryExpression -> {
        val lhsVal = lhs.unfoldExpression(context, builder)
        val rhsVal = rhs.unfoldExpression(context, builder)
        val resultReg = context.nextRegister()

        val op = when (operatorToken.type) {
            Token.Operator.Type.Add -> if (lhsVal.type == "double") "fadd" else "add"
            Token.Operator.Type.Subtract -> if (lhsVal.type == "double") "fsub" else "sub"
            Token.Operator.Type.Multiply -> if (lhsVal.type == "double") "fmul" else "mul"
            Token.Operator.Type.Divide -> if (lhsVal.type == "double") "fdiv" else "sdiv"
            Token.Operator.Type.Equals -> if (lhsVal.type == "double") "fcmp oeq" else "icmp eq"
            Token.Operator.Type.NotEquals -> if (lhsVal.type == "double") "fcmp one" else "icmp ne"
            Token.Operator.Type.GraterThan -> if (lhsVal.type == "double") "fcmp ogt" else "icmp sgt"
            Token.Operator.Type.LessThan -> if (lhsVal.type == "double") "fcmp olt" else "icmp slt"
            Token.Operator.Type.GraterThanOrEquals -> if (lhsVal.type == "double") "fcmp oge" else "icmp sge"
            Token.Operator.Type.LessThanOrEquals -> if (lhsVal.type == "double") "fcmp ole" else "icmp sle"
            else -> throw Exception("Unsupported binary operator: ${operatorToken.type.literal}")
        }

        builder.append("  $resultReg = $op ${lhsVal.type} ${lhsVal.register}, ${rhsVal.register}\n")
        val resultType = if (op.startsWith("icmp") || op.startsWith("fcmp")) "i1" else lhsVal.type
        LlvmValue(resultReg, resultType)
    }

    is IfConditionExpression -> {
        val resultReg = context.nextRegister()
        // Determine result type. For simplicity, assuming both branches return i64 if it's used as expression.
        builder.append("  $resultReg = alloca i64\n")

        val endLabel = context.nextBlock("if_end")

        val currentElseLabel = context.nextBlock("if_else")
        val ifBodyLabel = context.nextBlock("if_body")

        val condVal =
            ifConditionDeclaration.ifConditionExpression.unfoldExpression(context, builder)
        builder.append("  br i1 ${condVal.register}, label %$ifBodyLabel, label %$currentElseLabel\n")

        builder.append("$ifBodyLabel:\n")
        // Note: For full support, we'd process statements in the block.
        // For this PoC, we assume if it's an expression, the block ends with an expression.
        var lastVal: LlvmValue? = null
        for (statement in ifConditionDeclaration.ifBody.statements) {
            if (statement is ExpressionStatement && statement == ifConditionDeclaration.ifBody.statements.last()) {
                lastVal = statement.expression.unfoldExpression(context, builder)
                builder.append("  store ${lastVal.type} ${lastVal.register}, ptr $resultReg\n")
            } else {
                statement.compile(context, builder)
            }
        }
        val lastLineBody = builder.toString().trimEnd().substringAfterLast('\n').trim()
        if (!lastLineBody.startsWith("ret ") && !lastLineBody.startsWith("br ")) {
            builder.append("  br label %$endLabel\n")
        }

        builder.append("$currentElseLabel:\n")
        if (elseDeclaration != null) {
            for (statement in elseDeclaration.elseBody.statements) {
                if (statement is ExpressionStatement && statement == elseDeclaration.elseBody.statements.last()) {
                    lastVal = statement.expression.unfoldExpression(context, builder)
                    builder.append("  store ${lastVal.type} ${lastVal.register}, ptr $resultReg\n")
                } else {
                    statement.compile(context, builder)
                }
            }
        }
        val lastLineElse = builder.toString().trimEnd().substringAfterLast('\n').trim()
        if (!lastLineElse.startsWith("ret ") && !lastLineElse.startsWith("br ")) {
            builder.append("  br label %$endLabel\n")
        }

        builder.append("$endLabel:\n")
        val finalReg = context.nextRegister()
        builder.append("  $finalReg = load i64, ptr $resultReg\n")
        LlvmValue(finalReg, "i64")
    }

    is FunctionCallExpression -> {
        if (arguments.isNotEmpty() && arguments.first().propertyNameToken != null) {
            // Struct instantiation!
            val structName = nameToken.value
            val structPtr = context.nextRegister()

            // Assume each property is 8 bytes (i64)
            val size = arguments.size * 8
            builder.append("  $structPtr = call ptr @malloc(i64 $size)\n")

            arguments.forEachIndexed { index, arg ->
                val argVal = arg.valueExpression.unfoldExpression(context, builder)
                val elementPtr = context.nextRegister()
                // Get pointer to the struct's field
                builder.append("  $elementPtr = getelementptr %$structName, ptr $structPtr, i32 0, i32 $index\n")
                // Store the value
                builder.append("  store ${argVal.type} ${argVal.register}, ptr $elementPtr\n")
            }
            LlvmValue(structPtr, "ptr")
        } else if (nameToken.value == "length" && arguments.size == 1) {
            val argVal = arguments.first().valueExpression.unfoldExpression(context, builder)
            // Assuming the argument is an array. Length is the first i64 field.
            val asPtr = if (argVal.type == "ptr") argVal.register else {
                val ptrReg = context.nextRegister()
                builder.append("  $ptrReg = inttoptr i64 ${argVal.register} to ptr\n")
                ptrReg
            }
            
            val lengthPtr = context.nextRegister()
            builder.append("  $lengthPtr = getelementptr %Array, ptr $asPtr, i32 0, i32 0\n")
            val lengthVal = context.nextRegister()
            builder.append("  $lengthVal = load i64, ptr $lengthPtr\n")
            LlvmValue(lengthVal, "i64")
        } else if ((nameToken.value == "max" || nameToken.value == "maxOf") && arguments.size == 2) {
            val arg1 = arguments[0].valueExpression.unfoldExpression(context, builder)
            val arg2 = arguments[1].valueExpression.unfoldExpression(context, builder)
            val cmp = context.nextRegister()
            builder.append("  $cmp = icmp sgt i64 ${arg1.register}, ${arg2.register}\n")
            val resultReg = context.nextRegister()
            builder.append("  $resultReg = select i1 $cmp, i64 ${arg1.register}, i64 ${arg2.register}\n")
            LlvmValue(resultReg, "i64")
        } else if ((nameToken.value == "min" || nameToken.value == "minOf") && arguments.size == 2) {
            val arg1 = arguments[0].valueExpression.unfoldExpression(context, builder)
            val arg2 = arguments[1].valueExpression.unfoldExpression(context, builder)
            val cmp = context.nextRegister()
            builder.append("  $cmp = icmp slt i64 ${arg1.register}, ${arg2.register}\n")
            val resultReg = context.nextRegister()
            builder.append("  $resultReg = select i1 $cmp, i64 ${arg1.register}, i64 ${arg2.register}\n")
            LlvmValue(resultReg, "i64")
        } else {
            val sig = context.getFunctionSignature(nameToken.value)
            if (sig != null && sig.hasVararg) {
                // Package trailing arguments into an Array
                val fixedArgCount = sig.argCount - 1
                val fixedArgs = arguments.take(fixedArgCount).map { it.valueExpression.unfoldExpression(context, builder) }
                val varargs = arguments.drop(fixedArgCount).map { it.valueExpression.unfoldExpression(context, builder) }
                
                // Construct Array for varargs
                val arrayStructPtr = context.nextRegister()
                builder.append("  $arrayStructPtr = call ptr @malloc(i64 16)\n")
                val lengthPtr = context.nextRegister()
                builder.append("  $lengthPtr = getelementptr %Array, ptr $arrayStructPtr, i32 0, i32 0\n")
                builder.append("  store i64 ${varargs.size}, ptr $lengthPtr\n")
                
                val dataPtr = context.nextRegister()
                val dataSize = varargs.size * 8
                builder.append("  $dataPtr = call ptr @malloc(i64 $dataSize)\n")
                val bufferFieldPtr = context.nextRegister()
                builder.append("  $bufferFieldPtr = getelementptr %Array, ptr $arrayStructPtr, i32 0, i32 1\n")
                builder.append("  store ptr $dataPtr, ptr $bufferFieldPtr\n")
                
                varargs.forEachIndexed { index, llvmValue ->
                    val elemPtr = context.nextRegister()
                    builder.append("  $elemPtr = getelementptr i64, ptr $dataPtr, i32 $index\n")
                    if (llvmValue.type == "ptr") {
                        val asInt = context.nextRegister()
                        builder.append("  $asInt = ptrtoint ptr ${llvmValue.register} to i64\n")
                        builder.append("  store i64 $asInt, ptr $elemPtr\n")
                    } else if (llvmValue.type == "i1") {
                        val asInt = context.nextRegister()
                        builder.append("  $asInt = zext i1 ${llvmValue.register} to i64\n")
                        builder.append("  store i64 $asInt, ptr $elemPtr\n")
                    } else if (llvmValue.type == "double") {
                        val asInt = context.nextRegister()
                        builder.append("  $asInt = bitcast double ${llvmValue.register} to i64\n")
                        builder.append("  store i64 $asInt, ptr $elemPtr\n")
                    } else {
                        builder.append("  store i64 ${llvmValue.register}, ptr $elemPtr\n")
                    }
                }
                
                // All function parameters are i64 in this PoC
                val arrayAsInt = context.nextRegister()
                builder.append("  $arrayAsInt = ptrtoint ptr $arrayStructPtr to i64\n")
                
                val finalArgTypesStr = fixedArgs.joinToString(", ") { "${it.type} ${it.register}" } +
                    (if (fixedArgs.isNotEmpty()) ", " else "") + "i64 $arrayAsInt"
                    
                val resultReg = context.nextRegister()
                builder.append("  $resultReg = call i64 @${nameToken.value}($finalArgTypesStr)\n")
                LlvmValue(resultReg, "i64")
            } else {
                // Function call
                val argVals = arguments.map { it.valueExpression.unfoldExpression(context, builder) }
                val argTypesStr = argVals.joinToString(", ") { "${it.type} ${it.register}" }
                val resultReg = context.nextRegister()
                // Assuming all functions return i64 for this PoC
                builder.append("  $resultReg = call i64 @${nameToken.value}($argTypesStr)\n")
                LlvmValue(resultReg, "i64")
            }
        }
    }

    is ArrayLiteralExpression -> {
        // Evaluate all elements
        val elementVals = elements.map { it.unfoldExpression(context, builder) }

        // Allocate the %Array struct
        val arrayStructPtr = context.nextRegister()
        builder.append("  $arrayStructPtr = call ptr @malloc(i64 16)\n") // i64 + ptr = 16 bytes

        // Set the length
        val lengthPtr = context.nextRegister()
        builder.append("  $lengthPtr = getelementptr %Array, ptr $arrayStructPtr, i32 0, i32 0\n")
        builder.append("  store i64 ${elementVals.size}, ptr $lengthPtr\n")

        // Allocate the buffer for elements (each is i64)
        val dataPtr = context.nextRegister()
        val dataSize = elementVals.size * 8
        builder.append("  $dataPtr = call ptr @malloc(i64 $dataSize)\n")

        // Set the buffer pointer
        val bufferFieldPtr = context.nextRegister()
        builder.append("  $bufferFieldPtr = getelementptr %Array, ptr $arrayStructPtr, i32 0, i32 1\n")
        builder.append("  store ptr $dataPtr, ptr $bufferFieldPtr\n")

        // Store elements
        elementVals.forEachIndexed { index, llvmValue ->
            val elemPtr = context.nextRegister()
            builder.append("  $elemPtr = getelementptr i64, ptr $dataPtr, i32 $index\n")
            if (llvmValue.type == "ptr") {
                val asInt = context.nextRegister()
                builder.append("  $asInt = ptrtoint ptr ${llvmValue.register} to i64\n")
                builder.append("  store i64 $asInt, ptr $elemPtr\n")
            } else if (llvmValue.type == "i1") {
                val asInt = context.nextRegister()
                builder.append("  $asInt = zext i1 ${llvmValue.register} to i64\n")
                builder.append("  store i64 $asInt, ptr $elemPtr\n")
            } else if (llvmValue.type == "double") {
                val asInt = context.nextRegister()
                builder.append("  $asInt = bitcast double ${llvmValue.register} to i64\n")
                builder.append("  store i64 $asInt, ptr $elemPtr\n")
            } else {
                builder.append("  store i64 ${llvmValue.register}, ptr $elemPtr\n")
            }
        }

        LlvmValue(arrayStructPtr, "ptr")
    }

    is SetLiteralExpression -> {
        // Evaluate all elements
        val elementVals = elements.map { it.unfoldExpression(context, builder) }

        // Allocate the %Set struct
        val setStructPtr = context.nextRegister()
        builder.append("  $setStructPtr = call ptr @malloc(i64 16)\n") // i64 + ptr = 16 bytes

        // Set the length
        val lengthPtr = context.nextRegister()
        builder.append("  $lengthPtr = getelementptr %Set, ptr $setStructPtr, i32 0, i32 0\n")
        builder.append("  store i64 ${elementVals.size}, ptr $lengthPtr\n")

        // Allocate the buffer for elements (each is i64)
        val dataPtr = context.nextRegister()
        val dataSize = elementVals.size * 8
        builder.append("  $dataPtr = call ptr @malloc(i64 $dataSize)\n")

        // Set the buffer pointer
        val bufferFieldPtr = context.nextRegister()
        builder.append("  $bufferFieldPtr = getelementptr %Set, ptr $setStructPtr, i32 0, i32 1\n")
        builder.append("  store ptr $dataPtr, ptr $bufferFieldPtr\n")

        // Store elements
        elementVals.forEachIndexed { index, llvmValue ->
            val elemPtr = context.nextRegister()
            builder.append("  $elemPtr = getelementptr i64, ptr $dataPtr, i32 $index\n")
            if (llvmValue.type == "ptr") {
                val asInt = context.nextRegister()
                builder.append("  $asInt = ptrtoint ptr ${llvmValue.register} to i64\n")
                builder.append("  store i64 $asInt, ptr $elemPtr\n")
            } else if (llvmValue.type == "i1") {
                val asInt = context.nextRegister()
                builder.append("  $asInt = zext i1 ${llvmValue.register} to i64\n")
                builder.append("  store i64 $asInt, ptr $elemPtr\n")
            } else if (llvmValue.type == "double") {
                val asInt = context.nextRegister()
                builder.append("  $asInt = bitcast double ${llvmValue.register} to i64\n")
                builder.append("  store i64 $asInt, ptr $elemPtr\n")
            } else {
                builder.append("  store i64 ${llvmValue.register}, ptr $elemPtr\n")
            }
        }

        LlvmValue(setStructPtr, "ptr")
    }
    
    is MapLiteralExpression -> {
        // Evaluate all elements
        val keyVals = keyValuePairs.map { it.keyExpression.unfoldExpression(context, builder) }
        val valueVals = keyValuePairs.map { it.valueExpression.unfoldExpression(context, builder) }

        // Allocate the %Map struct
        val mapStructPtr = context.nextRegister()
        builder.append("  $mapStructPtr = call ptr @malloc(i64 24)\n") // i64 + ptr + ptr = 24 bytes

        // Set the length
        val lengthPtr = context.nextRegister()
        builder.append("  $lengthPtr = getelementptr %Map, ptr $mapStructPtr, i32 0, i32 0\n")
        builder.append("  store i64 ${keyVals.size}, ptr $lengthPtr\n")

        // Allocate the buffer for keys (each is i64)
        val keysDataPtr = context.nextRegister()
        val dataSize = keyVals.size * 8
        builder.append("  $keysDataPtr = call ptr @malloc(i64 $dataSize)\n")

        // Set the keys buffer pointer
        val keysBufferFieldPtr = context.nextRegister()
        builder.append("  $keysBufferFieldPtr = getelementptr %Map, ptr $mapStructPtr, i32 0, i32 1\n")
        builder.append("  store ptr $keysDataPtr, ptr $keysBufferFieldPtr\n")

        // Allocate the buffer for values (each is i64)
        val valuesDataPtr = context.nextRegister()
        builder.append("  $valuesDataPtr = call ptr @malloc(i64 $dataSize)\n")

        // Set the values buffer pointer
        val valuesBufferFieldPtr = context.nextRegister()
        builder.append("  $valuesBufferFieldPtr = getelementptr %Map, ptr $mapStructPtr, i32 0, i32 2\n")
        builder.append("  store ptr $valuesDataPtr, ptr $valuesBufferFieldPtr\n")

        // Store elements
        keyVals.forEachIndexed { index, llvmValue ->
            val elemPtr = context.nextRegister()
            builder.append("  $elemPtr = getelementptr i64, ptr $keysDataPtr, i32 $index\n")
            if (llvmValue.type == "ptr") {
                val asInt = context.nextRegister()
                builder.append("  $asInt = ptrtoint ptr ${llvmValue.register} to i64\n")
                builder.append("  store i64 $asInt, ptr $elemPtr\n")
            } else if (llvmValue.type == "i1") {
                val asInt = context.nextRegister()
                builder.append("  $asInt = zext i1 ${llvmValue.register} to i64\n")
                builder.append("  store i64 $asInt, ptr $elemPtr\n")
            } else if (llvmValue.type == "double") {
                val asInt = context.nextRegister()
                builder.append("  $asInt = bitcast double ${llvmValue.register} to i64\n")
                builder.append("  store i64 $asInt, ptr $elemPtr\n")
            } else {
                builder.append("  store i64 ${llvmValue.register}, ptr $elemPtr\n")
            }
        }
        
        valueVals.forEachIndexed { index, llvmValue ->
            val elemPtr = context.nextRegister()
            builder.append("  $elemPtr = getelementptr i64, ptr $valuesDataPtr, i32 $index\n")
            if (llvmValue.type == "ptr") {
                val asInt = context.nextRegister()
                builder.append("  $asInt = ptrtoint ptr ${llvmValue.register} to i64\n")
                builder.append("  store i64 $asInt, ptr $elemPtr\n")
            } else if (llvmValue.type == "i1") {
                val asInt = context.nextRegister()
                builder.append("  $asInt = zext i1 ${llvmValue.register} to i64\n")
                builder.append("  store i64 $asInt, ptr $elemPtr\n")
            } else if (llvmValue.type == "double") {
                val asInt = context.nextRegister()
                builder.append("  $asInt = bitcast double ${llvmValue.register} to i64\n")
                builder.append("  store i64 $asInt, ptr $elemPtr\n")
            } else {
                builder.append("  store i64 ${llvmValue.register}, ptr $elemPtr\n")
            }
        }

        LlvmValue(mapStructPtr, "ptr")
    }

    is com.pointlessapps.granite.mica.ast.expressions.MemberAccessExpression -> {
        val symbolVal = symbolExpression.unfoldExpression(context, builder)
        val asPtr = if (symbolVal.type == "ptr") symbolVal.register else {
            val ptrReg = context.nextRegister()
            builder.append("  $ptrReg = inttoptr i64 ${symbolVal.register} to ptr\n")
            ptrReg
        }
        
        // Assume single accessor for this PoC
        val accessor = accessorExpressions.first()
        if (accessor is com.pointlessapps.granite.mica.ast.ArrayIndexAccessorExpression) {
            val indexVal = accessor.indexExpression.unfoldExpression(context, builder)
            
            // Get array buffer pointer
            val bufferPtr = context.nextRegister()
            builder.append("  $bufferPtr = getelementptr %Array, ptr $asPtr, i32 0, i32 1\n")
            val bufferData = context.nextRegister()
            builder.append("  $bufferData = load ptr, ptr $bufferPtr\n")
            
            // Get pointer to element at index
            val elemPtr = context.nextRegister()
            builder.append("  $elemPtr = getelementptr i64, ptr $bufferData, i64 ${indexVal.register}\n")
            
            // Load value
            val resultReg = context.nextRegister()
            builder.append("  $resultReg = load i64, ptr $elemPtr\n")
            
            LlvmValue(resultReg, "i64") // Returning as i64 for PoC, wait, what if it's a string?
            // Since we don't carry type info in arrays yet, we just return i64.
        } else if (accessor is com.pointlessapps.granite.mica.ast.PropertyAccessAccessorExpression) {
            val propName = accessor.propertySymbolToken.value
            val structInfo = context.getStructPropertyIndex(propName) 
                ?: throw Exception("Could not find property $propName in any declared struct")
            
            val elemPtr = context.nextRegister()
            builder.append("  $elemPtr = getelementptr %${structInfo.first}, ptr $asPtr, i32 0, i32 ${structInfo.second}\n")
            val resultReg = context.nextRegister()
            builder.append("  $resultReg = load i64, ptr $elemPtr\n")
            LlvmValue(resultReg, "i64")
        } else {
            throw Exception("Unsupported accessor expression: ${accessor::class.simpleName}")
        }
    }
    is TypeCoercionExpression -> {
        lhs.unfoldExpression(context, builder)
    }

    is AffixAssignmentExpression -> {
        val ptr = context.getVariablePtr(symbolToken.value)
            ?: throw Exception("Undefined variable ${symbolToken.value}")
            
        if (accessorExpressions.isNotEmpty()) {
            throw Exception("Affix assignment with accessors not implemented yet in PoC")
        }
        
        val isIncrement = operatorToken is com.pointlessapps.granite.mica.model.Token.Increment
        val currentVal = context.nextRegister()
        builder.append("  $currentVal = load i64, ptr $ptr\n")
        
        val newVal = context.nextRegister()
        val op = if (isIncrement) "add" else "sub"
        builder.append("  $newVal = $op i64 $currentVal, 1\n")
        builder.append("  store i64 $newVal, ptr $ptr\n")
        
        if (this is PostfixAssignmentExpression) {
            LlvmValue(currentVal, "i64")
        } else {
            LlvmValue(newVal, "i64")
        }
    }
    else -> throw Exception("Unsupported expression type in LLVM compiler: ${this::class.simpleName}")
}
